import {
  estadoAdiantamentoMdfVazio,
  estadoProdutoAdiantamentoVazio,
  isFuncaoAdiantamentoMdf,
  type EstadoAdiantamentoMdf,
  type EstadoProdutoAdiantamentoMdf,
  type ExtraAdiantamentoMdf,
} from './adiantamentoMdfTipos';
import { descricaoPecaSemCor } from './resolverProdutoPontuacao';

export const LS_ADIANTAMENTO_MDF = 'ade-adiantamento-mdf-v1';
export const ADIANTAMENTO_MDF_DB = 'ade-adiantamento-mdf-db';
export const ADIANTAMENTO_MDF_DB_VERSION = 1;
const STORE = 'estado';
const KEY = 'atual';

function normalizarExtra(raw: unknown): ExtraAdiantamentoMdf | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const id = String(p.id || '').trim();
  if (!id) return null;
  return {
    id,
    descricao: descricaoPecaSemCor(String(p.descricao || '')),
    material: String(p.material || 'MDF').toUpperCase(),
    funcao: isFuncaoAdiantamentoMdf(p.funcao) ? p.funcao : '',
  };
}

function idsUnicos(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => String(x || '').trim()).filter(Boolean))];
}

function normalizarEstadoProduto(raw: unknown): EstadoProdutoAdiantamentoMdf {
  const base = estadoProdutoAdiantamentoVazio();
  if (!raw || typeof raw !== 'object') return base;
  const p = raw as Record<string, unknown>;
  return {
    ocultos: idsUnicos(p.ocultos),
    incluidos: idsUnicos(p.incluidos),
    extras: Array.isArray(p.extras)
      ? p.extras.map(normalizarExtra).filter((x): x is ExtraAdiantamentoMdf => x != null)
      : [],
  };
}

function normalizarEstado(raw: unknown): EstadoAdiantamentoMdf {
  const base = estadoAdiantamentoMdfVazio();
  if (!raw || typeof raw !== 'object') return base;
  const e = raw as Partial<EstadoAdiantamentoMdf> & Record<string, unknown>;
  const funcoesPorPeca: EstadoAdiantamentoMdf['funcoesPorPeca'] = {};
  if (e.funcoesPorPeca && typeof e.funcoesPorPeca === 'object') {
    for (const [k, v] of Object.entries(e.funcoesPorPeca)) {
      funcoesPorPeca[k] = isFuncaoAdiantamentoMdf(v) ? v : '';
    }
  }
  const porProduto: EstadoAdiantamentoMdf['porProduto'] = {};
  if (e.porProduto && typeof e.porProduto === 'object') {
    for (const [k, v] of Object.entries(e.porProduto)) {
      porProduto[k] = normalizarEstadoProduto(v);
    }
  }
  const datasPorLinha: Record<string, string> = {};
  if (e.datasPorLinha && typeof e.datasPorLinha === 'object') {
    for (const [k, v] of Object.entries(e.datasPorLinha)) {
      const dia = String(v || '').slice(0, 10);
      if (k && /^\d{4}-\d{2}-\d{2}$/.test(dia)) datasPorLinha[k] = dia;
    }
  }
  return {
    funcoesPorPeca,
    porProduto,
    produtoIdsNaLista: idsUnicos(e.produtoIdsNaLista),
    datasPorLinha,
    dataInicioFiltro: String(e.dataInicioFiltro || '').slice(0, 10),
    salvoEm: String(e.salvoEm || ''),
  };
}

function score(e: EstadoAdiantamentoMdf | null): number {
  if (!e) return -1;
  let n = Object.keys(e.funcoesPorPeca).length;
  for (const p of Object.values(e.porProduto || {})) {
    n += (p.ocultos?.length || 0) + (p.incluidos?.length || 0) + (p.extras?.length || 0) * 2;
  }
  n += (e.produtoIdsNaLista?.length || 0) * 5;
  n += Object.keys(e.datasPorLinha || {}).length;
  return n;
}

function abrirDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(ADIANTAMENTO_MDF_DB, ADIANTAMENTO_MDF_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function lerLs(): EstadoAdiantamentoMdf | null {
  try {
    const raw = localStorage.getItem(LS_ADIANTAMENTO_MDF);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    if (!p || typeof p !== 'object') return null;
    if (p.porProduto && typeof p.porProduto === 'object') return normalizarEstado(p);
    if (p.funcoesPorPeca && typeof p.funcoesPorPeca === 'object') return normalizarEstado(p);
    return null;
  } catch {
    return null;
  }
}

function gravarLsResumo(estado: EstadoAdiantamentoMdf): void {
  const resumo = {
    salvoEm: estado.salvoEm,
    dataInicioFiltro: estado.dataInicioFiltro,
    produtosNaLista: estado.produtoIdsNaLista.length,
    funcoesCount: Object.keys(estado.funcoesPorPeca).length,
  };
  try {
    localStorage.setItem(LS_ADIANTAMENTO_MDF, JSON.stringify(resumo));
  } catch {
    try {
      localStorage.removeItem(LS_ADIANTAMENTO_MDF);
    } catch {
      /* quota */
    }
  }
}

async function lerIdb(): Promise<EstadoAdiantamentoMdf | null> {
  try {
    const db = await abrirDb();
    const data = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(KEY);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    db.close();
    return data ? normalizarEstado(data) : null;
  } catch {
    return null;
  }
}

async function gravarIdb(estado: EstadoAdiantamentoMdf): Promise<void> {
  try {
    const db = await abrirDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(estado, KEY);
    });
    db.close();
  } catch {
    /* ignora */
  }
}

function preferir(a: EstadoAdiantamentoMdf | null, b: EstadoAdiantamentoMdf | null): EstadoAdiantamentoMdf {
  const sa = score(a);
  const sb = score(b);
  if (sa < 0 && sb < 0) return estadoAdiantamentoMdfVazio();
  if (sa < 0) return b as EstadoAdiantamentoMdf;
  if (sb < 0) return a as EstadoAdiantamentoMdf;
  if (sb > sa) return b as EstadoAdiantamentoMdf;
  if (sa > sb) return a as EstadoAdiantamentoMdf;
  const ta = Date.parse(a?.salvoEm || '') || 0;
  const tb = Date.parse(b?.salvoEm || '') || 0;
  return tb > ta ? (b as EstadoAdiantamentoMdf) : (a as EstadoAdiantamentoMdf);
}

export async function hidratarAdiantamentoMdf(): Promise<EstadoAdiantamentoMdf> {
  const ls = lerLs();
  const idb = await lerIdb();
  const estado = preferir(ls, idb);
  await gravarIdb(estado);
  gravarLsResumo(estado);
  return estado;
}

export async function salvarAdiantamentoMdf(estado: EstadoAdiantamentoMdf): Promise<void> {
  const next = { ...estado, salvoEm: new Date().toISOString() };
  await gravarIdb(next);
  gravarLsResumo(next);
}
